import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Switch,
  KeyboardAvoidingView,
  ScrollView,
  Platform,
  ActivityIndicator,
  StyleSheet,
  PixelRatio,
  Dimensions,
  Modal,
} from "react-native";

import Colours from "../../utils/Colours";
import { useNavigation, useRoute } from "@react-navigation/native";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import axios, { axiosWithCredentials } from "../../api/axios";

const { width, height } = Dimensions.get("window");

// --- RESPONSIVE TOOLKIT ---
const BASE_WIDTH = 375;
const BASE_HEIGHT = 812;

const dynamicFontSize = (size) => {
  const scaleFactor = Math.min(width / BASE_WIDTH, height / BASE_HEIGHT);
  return Math.round(PixelRatio.roundToNearestPixel(size * scaleFactor));
};

const dynamicSpacing = (size) => (width / BASE_WIDTH) * size;
const dynamicSize = (size) => (width / BASE_WIDTH) * size;

const VirtualTableScreen = () => {
  const navigation = useNavigation();
  const route = useRoute();

  const qrToken = route?.params?.qrToken;
  const isTopUp = route?.params?.isTopUp || false;

  const [isAutoCreating, setIsAutoCreating] = useState(false);

  /* ----------------------------------------------------
   * 1. QR CHECK-IN HOOK: Fetch location & brand details
   * ---------------------------------------------------- */
  const fetchQRDetails = async (token) => {
    if (!token) return null;
    const response = await axios.get(`/getVerifyQR/getVerifyQR/${token}`);
    return response.data;
  };

  const { data: qrLocation, isLoading: isLoadingQR } = useQuery({
    queryKey: ["verifyQR", qrToken],
    queryFn: () => fetchQRDetails(qrToken),
    enabled: !!qrToken,
    staleTime: 1000 * 60 * 60,
  });

  const brandID = qrLocation?.brandID;
  const waiterID = qrLocation?.waiterID;
  const tableLocation = qrLocation?.tableLocation;

  console.log("tableLocation", tableLocation);
  console.log("waiterID", waiterID);
  console.log("brandID", brandID);

  /* ----------------------------------------------------
   * 2. FETCH ACTIVE SHOW FOR VENUE
   * ---------------------------------------------------- */
  const fetchActiveShow = async (brandId) => {
    if (!brandId) return null;
    const response = await axios.get(`/getActiveShow/getActiveShow/${brandId}`);
    return response.data?.show || null;
  };

  const { data: activeShow, isLoading: isLoadingShow } = useQuery({
    queryKey: ["activeShow", brandID],
    queryFn: () => fetchActiveShow(brandID),
    enabled: !!brandID,
    staleTime: 1000 * 60 * 5,
  });

  /* ----------------------------------------------------
   * 3. FETCH NEXT TABLE NUMBER
   * ---------------------------------------------------- */
  const fetchNextTableNumber = async (brandId) => {
    if (!brandId) return null;
    const response = await axios.get(`/getNextTableNumber/getNextTableNumber/${brandId}`);
    return response.data?.tableNumber;
  };

  const { data: assignedTableNumber } = useQuery({
    queryKey: ["nextTableNumber", brandID],
    queryFn: () => fetchNextTableNumber(brandID),
    enabled: !!brandID,
    staleTime: 0,
  });

  /* ----------------------------------------------------
   * 4. AUTOMATED BACKGROUND TABLE CREATION & REDIRECT
   * ---------------------------------------------------- */
  useEffect(() => {
    // Wait until QR validation completes
    if (isLoadingQR || isLoadingShow) return;

    // Handle invalid or missing QR parameters safely
    // if (!brandID || !waiterID || brandID === "undefined" || waiterID === "undefined") {
    if (!brandID || brandID === "undefined") {
      navigation.reset({
        index: 0,
        routes: [{ name: "getStarted" }],
      });
      return;
    }

    // FIXED: Wait until assignedTableNumber is defined before triggering table creation
    // if (brandID && waiterID && assignedTableNumber !== undefined && !isAutoCreating) {
    if (brandID && assignedTableNumber !== undefined && !isAutoCreating) {
      autoCreateVirtualTableAndRedirect();
    }
  }, [brandID, waiterID, assignedTableNumber, isLoadingQR, isLoadingShow, isAutoCreating]);

  const autoCreateVirtualTableAndRedirect = async () => {
    // Extra safety check
    if (assignedTableNumber === undefined || assignedTableNumber === null) {
      console.warn("Attempted to auto-create table before assignedTableNumber was loaded.");
      return;
    }
    setIsAutoCreating(true);

    const autoPayload = {
      tableName: `Table_${assignedTableNumber}`, // Default automated table title
      // tableName: "Guest Table", // Default automated table title
      tableAnonymity: false, // Default to anonymous session
      tableNumber: assignedTableNumber,
      associatedBrand: brandID,
      showId: activeShow?._id || null,
      waiterID,
      tableLocation,
    };

    try {
      const response = await axios.post("/createVirtualTable/createVirtualTable", autoPayload);

      if (response.data?.success && response.data?.tableData) {
        const { tableData } = response.data;
        const generatedTableId = tableData._id || tableData.id;

        // Reset state & land directly on BrandMenu without signup/table form friction
        navigation.reset({
          index: 0,
          routes: [
            {
              name: "brandMenu",
              params: {
                tableId: generatedTableId,
                tableAnonymity: true,
                isTopUp: isTopUp,
                flexTier: "regular",
                brandID: brandID,
              },
            },
          ],
        });
      } else {
        throw new Error("Invalid table setup payload returned");
      }
    } catch (error) {
      console.error("Automated background table creation failed:", error);
      // Fallback fallback routing in case of server failure
      navigation.reset({
        index: 0,
        routes: [{ name: "getStarted" }],
      });
    } finally {
      setIsAutoCreating(false);
    }
  };

  return (
    <View style={styles.container}>
      <ActivityIndicator size="large" color={Colours.PRIMARY || "#000"} />
      <Text style={styles.loadingText}>Setting up your table menu...</Text>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    backgroundColor: "#FFFFFF",
  },
  loadingText: {
    marginTop: dynamicSpacing(14),
    fontSize: dynamicFontSize(15),
    fontFamily: "Sen-Regular",
    color: "#4A4A4A",
  },
});

export default VirtualTableScreen;
